/**
 * Tags list: create tag, see status/assignment, quick totals.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

import api, { extractErr } from "../api";
import {
    Badge,
    Button,
    Card,
    EmptyState,
    Field,
    Input,
    PageHeader,
    formatDate,
} from "../ui";

export default function TagsList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ code: "", description: "" });
    const [err, setErr] = useState("");

    const reload = () => {
        setLoading(true);
        api.get("/stats/tags")
            .then((r) => setRows(r.data))
            .catch((e) => setErr(extractErr(e)))
            .finally(() => setLoading(false));
    };
    useEffect(reload, []);

    const create = async (e) => {
        e.preventDefault();
        setErr("");
        try {
            await api.post("/tags", { ...form, code: form.code.trim().toUpperCase(), active: true });
            setForm({ code: "", description: "" });
            setCreating(false);
            reload();
        } catch (e) {
            setErr(extractErr(e));
        }
    };

    return (
        <div data-testid="tags-list-page">
            <PageHeader
                title="Tag NFC"
                description="Ogni tag ha un codice permanente (es. SM001). Puoi riassegnarlo a collaboratori diversi nel tempo senza riprogrammare il chip."
                actions={
                    <Button onClick={() => setCreating((c) => !c)} data-testid="tags-new-toggle">
                        <Plus size={14} /> Nuovo tag
                    </Button>
                }
            />

            {creating && (
                <Card className="p-5 mb-6">
                    <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label="Codice" hint="Solo lettere, numeri, - e _. Esempio: SM001">
                            <Input
                                required
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                pattern="[A-Za-z0-9_\-]+"
                                data-testid="tag-form-code"
                            />
                        </Field>
                        <Field label="Descrizione (opzionale)">
                            <Input
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                data-testid="tag-form-description"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Button type="submit" data-testid="tag-form-submit">Crea tag</Button>
                        </div>
                    </form>
                </Card>
            )}

            {err && <div className="text-red-600 text-sm mb-3">{err}</div>}

            <Card className="overflow-hidden">
                {loading ? (
                    <div className="p-8 text-slate-500 text-sm">Caricamento…</div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nessun tag registrato"
                        description="Crea il primo tag NFC per iniziare a raccogliere accessi."
                        action={<Button onClick={() => setCreating(true)}>Crea tag</Button>}
                    />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Stato</th>
                                <th className="text-left px-5 py-3">Collaboratore</th>
                                <th className="text-right px-5 py-3">30 gg</th>
                                <th className="text-right px-5 py-3">Totale</th>
                                <th className="text-left px-5 py-3">Ultima interazione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.code} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/admin/tag/${r.code}`}
                                            className="font-mono font-medium hover:underline"
                                            data-testid={`tag-row-${r.code}`}
                                        >
                                            {r.code}
                                        </Link>
                                    </td>
                                    <td className="px-5 py-3">
                                        {r.active ? (
                                            <Badge tone="success">attivo</Badge>
                                        ) : (
                                            <Badge tone="danger">disattivato</Badge>
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        {r.current_staff_name ? (
                                            <Link
                                                to={`/admin/collaboratori/${r.current_staff_id}`}
                                                className="hover:underline"
                                            >
                                                {r.current_staff_name}
                                            </Link>
                                        ) : (
                                            <span className="text-slate-400">non assegnato</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right tabular-nums">{r.last_30d}</td>
                                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                                        {r.total}
                                    </td>
                                    <td className="px-5 py-3 text-slate-500">{formatDate(r.last_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}
